import { type ReactNode, useEffect, useRef, useState } from 'react';

/**
 * A button that opens a small panel beside it, closed by Escape or a click
 * elsewhere. The shared shell for `OverflowMenu` and for contextual help —
 * information a page needs to hold without printing it under every table.
 */
export function Popover({
  trigger,
  children,
  align = 'right',
  panelClassName = '',
  closeOnClick = false,
}: {
  trigger: (props: { open: boolean; toggle: () => void }) => ReactNode;
  children: ReactNode;
  align?: 'left' | 'right';
  panelClassName?: string;
  /** Close when anything inside the panel is clicked — right for a menu, wrong for help text. */
  closeOnClick?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) {
      return;
    }
    const onPointer = (event: PointerEvent) => {
      if (root.current !== null && !root.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    };
    document.addEventListener('pointerdown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={root} className="relative">
      {trigger({
        open,
        toggle: () => {
          setOpen((value) => !value);
        },
      })}
      {open ? (
        <div
          className={`border-border bg-surface rounded-container absolute top-full z-(--z-overlay) mt-1 border shadow-lg ${
            align === 'right' ? 'right-0' : 'left-0'
          } ${panelClassName}`}
          {...(closeOnClick
            ? {
                onClick: () => {
                  setOpen(false);
                },
              }
            : {})}
        >
          {children}
        </div>
      ) : null}
    </div>
  );
}
